import React, { useState, useEffect } from 'react';
import { FaPlus, FaStar } from 'react-icons/fa';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ToastContainer, toast } from 'react-toastify';
import Slider from 'react-slick';
import 'react-toastify/dist/ReactToastify.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/Blog.css';

const Blog = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [blogs, setBlogs] = useState([]);

  // Fetch blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      const blogSnapshot = await getDocs(collection(db, 'blogs'));
      const blogList = blogSnapshot.docs.map(doc => doc.data());
      setBlogs(blogList);
    };
    fetchBlogs();
  }, []);

  const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const reader = new FileReader();

      reader.onload = (e) => { image.src = e.target.result; };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);

      image.onload = () => {
        const canvas = document.createElement('canvas');
        let width = image.width, height = image.height;
        if (width > height) {
          if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
        } else {
          if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: blob.type }));
          } else {
            reject(new Error('Compression failed'));
          }
        }, 'image/jpeg', quality);
      };
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setImage(compressed);
      setPreviewUrl(URL.createObjectURL(compressed));
    } catch {
      toast.error("Image compression failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image || !content || rating === 0) {
      toast.error('Please fill all fields!');
      return;
    }

    setLoading(true);

    try {
      const imageRef = ref(storage, `blogs/${image.name}`);
      await uploadBytes(imageRef, image);
      const imageUrl = await getDownloadURL(imageRef);

      const blogData = {
        imageUrl,
        content,
        rating,
        createdAt: new Date(),
      };

      await addDoc(collection(db, 'blogs'), blogData);
      setBlogs(prev => [blogData, ...prev]);

      toast.success('Blog added successfully!');
      setModalOpen(false);
      setImage(null);
      setPreviewUrl(null);
      setContent('');
      setRating(0);
    } catch {
      toast.error('Error saving blog.');
    } finally {
      setLoading(false);
    }
  };

  const settings = {
    dots: true,
    infinite: blogs.length > 3,
    speed: 500,
    slidesToShow: Math.min(3, blogs.length),
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  return (
    <div className="blog-page">
      <ToastContainer />
      <button className="add-blog-btn" onClick={() => setModalOpen(true)}>
        <FaPlus /> Add Blog
      </button>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add New Blog</h2>
            <form onSubmit={handleSubmit}>
              <label>Upload Your Profile</label>
              <input type="file" accept="image/*" onChange={handleImageChange} required />
              {previewUrl && <img src={previewUrl} alt="Preview" className="preview-img" />}

              <label>Rate this blog</label>
              <div className="star-rating">
                {[...Array(5)].map((_, index) => {
                  const currentRating = index + 1;
                  return (
                    <button
                      key={index}
                      type="button"
                      className={currentRating <= (hover || rating) ? 'on' : 'off'}
                      onClick={() => setRating(currentRating)}
                      onMouseEnter={() => setHover(currentRating)}
                      onMouseLeave={() => setHover(null)}
                    >
                      <FaStar />
                    </button>
                  );
                })}
              </div>

              <label>Blog Content (max 200 characters)</label>
              <textarea
                maxLength="200"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your blog content here..."
                required
              ></textarea>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? <span className="loader"></span> : 'Submit'}
              </button>
              <button type="button" className="cancel-btn" onClick={() => setModalOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {blogs.length > 0 && (
        <div className="slider-container">
          <h2 className="section-title">Latest Blogs</h2>
          <Slider {...settings}>
            {blogs.map((blog, index) => (
              <div className="blog-card" key={index}>
                <img src={blog.imageUrl} alt="blog" className="card-img" />
                <div className="card-content">
                  <div className="card-stars">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < blog.rating ? 'on' : 'off'} />
                    ))}
                  </div>
                  <p>{blog.content}</p>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default Blog;
